import {Divider, Grid, List, ListItem, ListItemText, makeStyles} from '@material-ui/core';
import {isLoaded} from 'Core/models/Async';
import {QuotingRouteConstants} from 'Quoting/Routes';
import {QuoteActionSource, clearSelectedQuotesForAction, createProposal} from 'Quoting/ducks/quoteActions';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {getCurrentDate, getFirstDateOfNextYear, getFormattedDate, getISODateString} from 'Quoting/utils/dateUtils';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {isDefined, safeRenderValue} from 'Quoting/utils/utilities';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {themeColors} from 'Theme';
import {DateTime} from 'luxon';
import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import {OpportunityTitle} from '../Opportunity/QuotingLandingPage';
import {QuoteDatePicker} from '../shared/QuoteDatePicker';
import {QuoteFormControlElement} from '../shared/QuoteFormControlElement';
import {QuoteTextField} from '../shared/QuoteTextField';
import {CardAction, QuotingCard} from '../shared/QuotingCard/QuotingCard';

const createProposalLabels = {
  select: '',
  name: 'Proposal',
  createdOn: 'Created on',
  expirationDate: 'Proposal Expiration Date',
  quotes: 'Quotes',
  products: 'Products',
};

const useCreateProposalStyles = makeStyles(theme => ({
  header: {
    paddingTop: '0.5rem',
    fontWeight: 600,
  },
  proposalContainer: {
    flexDirection: 'column',
  },
  proposalListContainer: {
    alignItems: 'center',
  },
  proposalRow: {
    alignItems: 'center',
    padding: '1rem 0',
    borderBottom: '1px solid #ccc',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  proposalNameContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '1rem',
  },
  capitalizeProposalButton: {
    textTransform: 'capitalize',
  },
  proposalList: {
    maxHeight: '12rem',
    [theme.breakpoints.up('xl')]: {
      maxHeight: '20rem',
    },
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '1.15rem',
    },
    '&::-webkit-scrollbar-track': {
      background: themeColors.gray_100,
      borderRadius: '0.35rem',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: themeColors.gray_400,
      border: '5px solid transparent',
      borderRadius: '8rem',
      backgroundClip: 'content-box',
    },
    margin: '0.25rem .25rem',
  },
}));

const ProposalListHeader = (props: {className: string}) => (
  <>
    <Grid container className={props.className}>
      {Object.values(createProposalLabels).map(label => (
        <Grid item key={label} xs={2}>
          {label}
        </Grid>
      ))}
    </Grid>
    <Divider style={{width: '96%', marginTop: '1rem'}} />
  </>
);

type NewProposal = {
  name: string;
  number: string;
  proposalExpirationDate: DateTime | null;
  editable: boolean;
  hasError?: boolean;
};

const NEW_EMPTY_PROPOSAL: NewProposal = {
  number: 'newProposal',
  name: '',
  proposalExpirationDate: getFirstDateOfNextYear(),
  editable: true,
  hasError: false,
};

const ERR_PROPOSAL_NAME_MANDATORY = {hasError: true, errorText: 'Proposal name is mandatory'};
const ERR_DUPLICATE_PROPOSAL_NAME = {
  hasError: true,
  errorText: 'Proposal with this name already exists. Please enter another name',
};

export const CreateProposalForm = () => {
  const [newProposal, setNewProposal] = useState<NewProposal>(NEW_EMPTY_PROPOSAL);
  const [selectedProposalNumber, setSelectedProposalNumber] = useState<string | null>(newProposal.number);
  const [error, setError] = useState<{hasError: boolean; errorText: string} | null>(null);

  const {opportunityId} = useParams<{opportunityId: string}>();

  const {proposals, opportunitySummary} = useSelector((state: ReducerState) => state.opportunity);

  const {selectedQuotesForAction, actionSource, selectedSingleQuoteForAction} = useSelector(
    (state: ReducerState) => state.quoteActions
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useCreateProposalStyles();

  const selectedQuotesForProposal =
    actionSource === QuoteActionSource.QuoteList && isDefined(selectedSingleQuoteForAction)
      ? [selectedSingleQuoteForAction]
      : Object.values(selectedQuotesForAction);

  const handleCreateNewProposal = useCallback(() => {
    if (newProposal && newProposal.number === selectedProposalNumber && isLoaded(opportunitySummary)) {
      const isExistingProposalName =
        selectedProposalNumber === newProposal.number &&
        newProposal.name !== '' &&
        isLoaded(proposals) &&
        Array.isArray(proposals.Data) &&
        proposals.Data?.some(p => p.name === newProposal.name);

      if (isExistingProposalName) {
        return setError(ERR_PROPOSAL_NAME_MANDATORY);
      }

      dispatch(
        createProposal(
          opportunityId ?? '',
          opportunitySummary.Data?._key?.rootId,
          opportunitySummary.Data?.customerNumber,
          selectedQuotesForProposal,
          {...newProposal, proposalExpirationDate: getISODateString(newProposal.proposalExpirationDate)}
        )
      );
    }
  }, [newProposal, selectedProposalNumber, dispatch]);

  const handleCreateProposalCancel = useCallback(() => {
    dispatch(clearSelectedQuotesForAction());
    history.push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(opportunityId ?? ''));
  }, [history, opportunityId]);

  const validateInputErrors = () => {
    if (selectedProposalNumber === newProposal.number && !newProposal.name) {
      return setError(ERR_PROPOSAL_NAME_MANDATORY);
    }

    const isExistingProposalName =
      selectedProposalNumber === newProposal.number &&
      newProposal.name !== '' &&
      isLoaded(proposals) &&
      Array.isArray(proposals.Data) &&
      proposals.Data?.some(p => p.name === newProposal.name);

    if (isExistingProposalName) {
      return setError(ERR_DUPLICATE_PROPOSAL_NAME);
    }
    return setError(null);
  };

  const footerActions: CardAction[] = useMemo(
    () => [
      {
        label: 'Cancel',
        handlerFn: handleCreateProposalCancel,
        props: {
          variant: 'contained',
          className: classes.capitalizeProposalButton,
        },
      },
      {
        label: 'Select',
        handlerFn: handleCreateNewProposal,
        props: {
          disabled:
            !selectedProposalNumber ||
            selectedProposalNumber !== newProposal.number ||
            (selectedProposalNumber === newProposal.number && newProposal.name === '') ||
            error?.hasError,
          variant: 'contained',
          color: 'primary',
          className: classes.capitalizeProposalButton,
        },
      },
    ],
    [
      handleCreateProposalCancel,
      handleCreateNewProposal,
      selectedProposalNumber,
      newProposal.number,
      newProposal.name,
      error?.hasError,
    ]
  );

  return (
    <QuotingCard footerActions={footerActions} title={<OpportunityTitle opportunityId={opportunityId ?? ''} />}>
      <ProposalListHeader className={classes.header} />
      <List className={classes.proposalList}>
        <>
          <ListItem key={newProposal.number}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2}>
                <QuoteFormControlElement
                  controlType="radio"
                  checked={selectedProposalNumber === newProposal.number}
                  value={newProposal.number}
                  onChange={e => {
                    setSelectedProposalNumber(e.target.value);
                    validateInputErrors();
                  }}
                  inputProps={{'aria-label': newProposal.name ?? newProposal.number}}
                />
              </Grid>
              <Grid item xs={2}>
                <QuoteTextField
                  id="new-proposal-name-input"
                  error={error?.hasError}
                  value={newProposal.name}
                  helperText={error?.hasError ? error.errorText : undefined}
                  onChange={e => setNewProposal({...newProposal, name: e.target.value})}
                  onBlur={validateInputErrors}
                  required
                />
              </Grid>
              <Grid item xs={2}>
                <ListItemText primary={getCurrentDate()} />
              </Grid>
              <Grid item xs={2}>
                <QuoteDatePicker
                  value={newProposal.proposalExpirationDate}
                  onChange={date => setNewProposal({...newProposal, proposalExpirationDate: date})}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <ListItemText primary={selectedQuotesForProposal?.map(quote => quote.policyNumber).join(', ')} />
              </Grid>
              <Grid item xs={2}>
                <ListItemText
                  primary={selectedQuotesForProposal
                    ?.map(quote => getProductCdFromModel(quote.productModel as A360ProductModel))
                    .join(', ')}
                />
              </Grid>
            </Grid>
          </ListItem>
          <Divider key={`divider-${newProposal.number}`} style={{width: '98%'}} />
        </>
        <>
          {renderAsyncView(proposals, proposalDetails =>
            Array.isArray(proposalDetails) ? (
              <>
                {proposalDetails?.map(proposal => (
                  <>
                    <ListItem key={proposal.number}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={2}>
                          <QuoteFormControlElement
                            controlType="radio"
                            checked={selectedProposalNumber === proposal.number}
                            value={proposal.number}
                            onChange={e => {
                              setError(null);
                              setSelectedProposalNumber(e.target.value);
                            }}
                            inputProps={{'aria-label': proposal.name ?? proposal.number}}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <ListItemText primary={safeRenderValue(proposal.name)} />
                        </Grid>
                        <Grid item xs={2}>
                          <ListItemText
                            primary={proposal.editable ? getCurrentDate() : getFormattedDate(proposal.createdOn)}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <ListItemText primary={getFormattedDate(proposal.proposalExpirationDate)} />
                        </Grid>
                        <Grid item xs={2}>
                          <ListItemText primary={proposal?.quotes?.map(quote => quote.policyNumber).join(', ')} />
                        </Grid>
                        <Grid item xs={2}>
                          <ListItemText primary={proposal?.quotes?.map(quote => quote.shortProductName).join(', ')} />
                        </Grid>
                      </Grid>
                    </ListItem>
                    <Divider key={`divider-${proposal.number}`} style={{width: '98%'}} />
                  </>
                ))}
              </>
            ) : (
              <></>
            )
          )}
        </>
      </List>
    </QuotingCard>
  );
};
